import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { purchasesPageStyles } from "./purchases-page.css";
import { rpc } from "ui-common/services/rpc";
import { AppContextController } from "ui-common/appContext";

@customElement("purchases-page")
export class PurchasesPage extends LitElement {
  static override styles = [purchasesPageStyles];

  private appContextController = new AppContextController(this);

  @state()
  private isRedirecting = false;

  @state()
  private redirectError = "";

  override connectedCallback() {
    super.connectedCallback();
    this.handleAuthTokenAndRedirect();
  }

  private async handleAuthTokenAndRedirect() {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get("authToken");

    // Handle auth token for cross-browser login after Stripe redirect
    if (authToken && !this.appContextController.appContext.currentAccessToken) {
      try {
        const result = await rpc("verify-checkout-auth-token", { token: authToken });
        if (result?.accessToken) {
          this.appContextController.appContext.signIn(result.accessToken);
        }
      } catch (error) {
        console.error("Failed to verify checkout auth token:", error);
        // Silent fallback - user can sign in manually
      }
    }

    // Clean authToken from URL immediately for security
    if (authToken) {
      params.delete("authToken");
      const newSearch = params.toString();
      const cleanUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    }

    // Continue with existing redirect logic
    this.handleSuccessRedirect();
  }

  private async handleSuccessRedirect() {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const contentType = params.get("contentType");
    const contentId = params.get("contentId");
    const handle = params.get("handle");

    if (success === "true" && handle) {
      this.isRedirecting = true;

      if (contentType === "post" && contentId) {
        window.location.href = `/${handle}?postId=${contentId}`;
      } else if (contentType === "link" && contentId) {
        window.location.href = `/${handle}`;
      } else {
        window.location.href = `/${handle}`;
      }
    } else if (success !== "true") {
      this.redirectError = "Purchase was not completed";
    }
  }

  override render() {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");

    if (this.redirectError) {
      return html`
        <div class="error-container">
          <p class="error-message">${this.redirectError}</p>
        </div>
      `;
    }

    if (success === "true" && this.isRedirecting) {
      return html`
        <div class="success-container">
          <div class="success-icon">✓</div>
          <h1 class="success-title">Purchase Successful!</h1>
          <p class="success-message">Thank you for your purchase. Redirecting you to your content...</p>
          <div class="spinner"></div>
        </div>
      `;
    }

    return html`
      <div class="error-container">
        <p class="error-message">No purchase information found</p>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "purchases-page": PurchasesPage;
  }
}
